class ProductRecommendations extends HTMLElement {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      const { attributeName } = mutation;
      if (!attributeName) continue;

      // Ignore error attribute changes
      if (attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // Cart drawer sync bumps these — do not re-enter #loadRecommendations.
      if (attributeName === 'data-section-id' || attributeName === 'data-skip-subtree-update') {
        continue;
      }

      if (attributeName === 'data-product-id') {
        const { productId } = this.dataset;
        if (productId && productId !== this.#lastLoadedProductId) {
          delete this.dataset.recommendationsPerformed;
        } else {
          continue;
        }
      }

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * Product id used for the last successful render
   * @type {string | undefined}
   */
  #lastLoadedProductId;

  /** @type {boolean} */
  #isLoading = false;

  connectedCallback() {
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });

    // Cart drawer loads on open only (see cart-drawer.js) — avoid fetching on every page view.
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const isCartDrawer = this.id === 'cart-drawer-you-may-like';
    const { productId, recommendationsPerformed, intent } = this.dataset;
    let { sectionId } = this.dataset;
    const id = this.id;

    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }

    if (isCartDrawer) {
      sectionId = sectionId || 'cart-drawer-cross-sell';
    }

    // If sectionId isn't available yet (common inside dialogs during cart morph),
    // don't fetch. We'll retry automatically when attributes update.
    if (!sectionId) {
      return;
    }

    // If the recommendations have already been loaded for this product, skip. Reload when product id changes.
    if (recommendationsPerformed === 'true' && this.#lastLoadedProductId === productId) {
      return;
    }

    if (this.#isLoading) {
      return;
    }

    const requestedProductId = productId;
    this.#isLoading = true;

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        this.#isLoading = false;
        if (this.dataset.productId !== requestedProductId) return;

        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);

        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
          this.#dedupeQuickAddComponents(recommendations);
          this.dataset.recommendationsPerformed = 'true';
          this.#lastLoadedProductId = requestedProductId;
          this.setAttribute('data-skip-subtree-update', '');
          this.innerHTML = recommendations.innerHTML;
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#isLoading = false;
        // Aborts are expected when inputs change quickly (e.g. quantity updates); don't treat as an error state.
        if (e?.name === 'AbortError') return;
        this.#handleError(e);
      });
  }

  /**
   * Cart drawer: load once when opened (IntersectionObserver is unreliable inside <dialog>).
   */
  loadCartDrawerRecommendations() {
    if (this.id !== 'cart-drawer-you-may-like') return;
    this.#loadRecommendations();
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Cart morph can append extra quick-add nodes; keep one per gallery before render.
   * @param {ParentNode} root
   */
  #dedupeQuickAddComponents(root) {
    root.querySelectorAll('product-card').forEach((card) => {
      card.querySelectorAll('.card-gallery').forEach((gallery) => {
        const components = gallery.querySelectorAll('quick-add-component');
        for (let i = 1; i < components.length; i++) {
          components[i].remove();
        }

        const quickAdd = gallery.querySelector('quick-add-component');
        const form = quickAdd?.querySelector('.quick-add__product-form-component');
        const addButton = quickAdd?.querySelector('.quick-add__button--add');
        const chooseButton = quickAdd?.querySelector('.quick-add__button--choose');

        if (!addButton || !chooseButton || !form) return;

        // options.size == 1 && variants.size > 1 renders both; keep one.
        if (form.classList.contains('quick-add__product-form-component--single-variant')) {
          chooseButton.remove();
        } else {
          addButton.remove();
        }
      });
    });
  }

  #handleError(error) {
    if (error?.name === 'AbortError') return;
    console.error('Product recommendations error:', error.message);
    // If sectionId is missing and we got a transient server error, don't permanently hide.
    // We'll retry when sectionId is populated.
    if (!this.dataset.sectionId && String(error?.message || '').includes('Server returned')) {
      return;
    }

    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
